import CWInput from '@/context/global/components/g00/g00-d00-g013-cw-input';
import { useApiStore } from '@/context/global/stores/apiStore';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import { TSong } from '@/context/global/types/song';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import SearchSongItem from './SearchSongItem';
import CWSpinner from '@/context/global/components/g00/g00-d00-g005-cw-spinner';
import IconSearch from '@/context/global/components/Icon/IconSearch';
import IconArrowLeft from '@/context/global/components/Icon/IconArrowLeft';
import IconSearchNotFound from '@/context/global/components/Icon/IconSearchNotFound';
import CurrentSong from './CurrentSong';

type SearchSongProps = {
  onBack?: () => void;
  currentSong?: TSong | null;
  onAddToQueue?: (song: TSong) => void;
};

const SearchSong = ({ onBack, currentSong, onAddToQueue }: SearchSongProps) => {
  const t = useTranslations('Karaoke.Sidebar');
  const { API } = useApiStore();
  const karaokeStore = useKaraokeStore();

  const [songs, setSongs] = useState<TSong[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleDebouncedSearch = async (value: string) => {
    setSearchQuery(value);
    if (!value) {
      setSongs([]);
      return;
    }

    try {
      setLoading(true);
      const response = await API.Page.Search({ searchWord: value });
      setSongs(response.data.responseObject.songResult.songs ?? []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToQueue = (song: TSong) => {
    karaokeStore.addToQueue(song);
    onAddToQueue?.(song);
  };

  const hasNoResults = !loading && searchQuery && songs.length === 0;

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4">
      {/* Current Song - Mobile only */}
      <div className="lg:hidden">
        <CurrentSong song={currentSong} isPlaying={false} />
      </div>

      {/* Mobile Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-2 py-2 text-white lg:hidden"
        >
          <IconArrowLeft className="size-5" />
          <span className="text-xl font-medium">{t('back')}</span>
        </button>
      )}

      <div className="hidden py-1.5 text-3xl font-bold lg:block">{t('searchSong')}</div>

      <div className="hidden h-0.5 bg-[#5E5E5E] lg:block" />

      <div>
        <CWInput
          prefixIcon={<IconSearch />}
          className="text-2xl font-medium"
          placeholder={t('searchSong')}
          onDebouncedChange={handleDebouncedSearch}
        />
      </div>

      {/* Search Results */}

      <div className="song-queue-list min-h-0 flex-1 overflow-y-auto">
        {loading && <CWSpinner />}
        {!loading &&
          songs.map((song) => (
            <SearchSongItem key={song.id} song={song} onAdd={handleAddToQueue} />
          ))}
        {hasNoResults && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <IconSearchNotFound className="mb-4 size-28 text-gray-500 lg:size-32" />
            <p className="text-lg font-bold text-white lg:text-xl">{t('noResults')}</p>
            <p className="mt-2 text-base text-gray-400 lg:text-lg">
              {t('noResultsDescription', { query: searchQuery })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchSong;
